/**
 * Trainer Meeting Tools — Student Q&A board.
 * Click a question to select it, then answer (or edit) inline.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Eye, EyeOff, MessageSquare, Pin } from "lucide-react";
import { fetchQaItems, patchQa } from "./liveCollaborationApi";
import type { QaItem } from "./liveCollaborationTypes";
import {
  COLLAB_PAGE_SIZE,
  mergeOlderById,
  oldestCreatedAt,
} from "./collabHistory";
import { useStickToBottomScroll } from "./useStickToBottomScroll";

type Props = {
  sessionId: string;
  trainerId: string;
  trainerName: string;
  items: QaItem[];
  /** Wait for room bootstrap attempt before any list REST. */
  hydrateReady?: boolean;
  /** Room collab/bootstrap succeeded — skip list REST even when empty. */
  skipHydrate?: boolean;
  joinedSince?: string;
  initialHasMore?: boolean;
  /** Select/scroll to this Q&A (e.g. from toast click). */
  focusQaId?: string | null;
  onFocusHandled?: () => void;
  onItems: (items: QaItem[]) => void;
  onPublish: (
    item: QaItem,
    opts?: { roomWide?: boolean; broadcast?: boolean },
  ) => Promise<void>;
};

function formatAskedAt(iso?: string | null): string {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function sortQaItems(items: QaItem[]): QaItem[] {
  return [...items].sort((a, b) => {
    const pin = Number(Boolean(b.pinned)) - Number(Boolean(a.pinned));
    if (pin !== 0) return pin;
    const aAt = Date.parse(String(a.created_at || "")) || 0;
    const bAt = Date.parse(String(b.created_at || "")) || 0;
    // Oldest → newest so new questions land at the bottom.
    return aAt - bAt;
  });
}

function authorLabel(item: QaItem): string {
  if (item.is_anonymous) return "Anonymous";
  return item.asked_by?.display_name || "Student";
}

function isAnswered(item: QaItem): boolean {
  return item.status === "answered" || Boolean(item.answer?.body);
}

function StatPill({
  label,
  value,
  accent,
  selected,
  onClick,
}: {
  label: string;
  value: string;
  accent: "amber" | "emerald";
  selected: boolean;
  onClick: () => void;
}) {
  const color = accent === "amber" ? "#fbbf24" : "#34d399";
  const selectedBorder =
    accent === "amber"
      ? "1px solid rgba(251,191,36,0.55)"
      : "1px solid rgba(52,211,153,0.55)";
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      style={{
        borderRadius: 12,
        border: selected ? selectedBorder : "1px solid rgba(255,255,255,0.1)",
        background: selected
          ? accent === "amber"
            ? "rgba(251,191,36,0.1)"
            : "rgba(52,211,153,0.1)"
          : "#1a1d26",
        padding: "8px 12px",
        textAlign: "center",
        cursor: "pointer",
        font: "inherit",
        color: "inherit",
      }}
    >
      <p style={{ margin: 0, fontSize: 18, fontWeight: 700, color }}>{value}</p>
      <p
        style={{
          margin: 0,
          fontSize: 10,
          color: selected ? color : "#64748b",
        }}
      >
        {label}
      </p>
    </button>
  );
}

function iconBtnStyle(
  active: boolean,
  activeBg: string,
  activeColor: string,
): React.CSSProperties {
  return {
    border: "none",
    borderRadius: 8,
    padding: 6,
    background: active ? activeBg : "transparent",
    color: active ? activeColor : "#64748b",
    cursor: "pointer",
  };
}

export function TrainerQaPanel({
  sessionId,
  trainerId,
  trainerName,
  items,
  hydrateReady = true,
  skipHydrate = false,
  joinedSince = "",
  initialHasMore = false,
  focusQaId = null,
  onFocusHandled,
  onItems,
  onPublish,
}: Props) {
  const [listFilter, setListFilter] = useState<"pending" | "answered">(
    "pending",
  );
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [answer, setAnswer] = useState("");
  const [busyId, setBusyId] = useState("");
  const [error, setError] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const cardRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const [hasMore, setHasMore] = useState(Boolean(initialHasMore));
  const [loadingOlder, setLoadingOlder] = useState(false);
  const loadingOlderRef = useRef(false);

  useEffect(() => {
    setHasMore(Boolean(initialHasMore));
  }, [initialHasMore, sessionId]);

  useEffect(() => {
    if (!hydrateReady) return;
    // Bootstrap already filled the list — rely on LiveKit + room REST poll.
    if (skipHydrate) return;
    let cancelled = false;
    void (async () => {
      try {
        // Recent page. Older questions load on scroll-up.
        const page = await fetchQaItems(sessionId, { limit: COLLAB_PAGE_SIZE });
        if (cancelled) return;
        onItems(
          mergeOlderById(
            page.items,
            itemsRef.current,
            (q) => q.qa_id,
          ),
        );
        setHasMore(page.hasMore);
      } catch (err) {
        console.error("fetchQaItems failed", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionId, hydrateReady, skipHydrate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Older pages: only when user scrolls (loadOlder), not an automatic cascade.

  useEffect(() => {
    const id = String(focusQaId || "").trim();
    if (!id) return;
    setSelectedId(id);
    const focused = itemsRef.current.find((q) => q.qa_id === id);
    if (focused) {
      setListFilter(isAnswered(focused) ? "answered" : "pending");
    }
    const el = cardRefs.current.get(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
    onFocusHandled?.();
  }, [focusQaId, items, onFocusHandled]);

  const visible = useMemo(
    () => items.filter((i) => i.status !== "dismissed"),
    [items],
  );

  const pending = useMemo(
    () => sortQaItems(visible.filter((q) => !isAnswered(q))),
    [visible],
  );

  const answered = useMemo(
    () => sortQaItems(visible.filter((q) => isAnswered(q))),
    [visible],
  );

  const listSignature = `${pending.length}:${answered.length}:${
    pending[pending.length - 1]?.qa_id || ""
  }:${answered[answered.length - 1]?.qa_id || ""}`;
  const loadOlderRef = useRef<() => void>(() => {});
  const { scrollerRef, bottomRef, onScroll, pinToBottom, restoreHeightRef } =
    useStickToBottomScroll([listSignature], {
      onNearTop: () => loadOlderRef.current(),
    });

  const loadOlder = useCallback(() => {
    if (loadingOlderRef.current || !hasMore) return;
    const before = oldestCreatedAt(itemsRef.current);
    if (!before) {
      setHasMore(false);
      return;
    }
    loadingOlderRef.current = true;
    setLoadingOlder(true);
    restoreHeightRef.current = scrollerRef.current?.scrollHeight ?? null;
    void (async () => {
      try {
        const page = await fetchQaItems(sessionId, { before });
        onItems(mergeOlderById(page.items, itemsRef.current, (q) => q.qa_id));
        setHasMore(page.hasMore);
      } catch (err) {
        console.error("load older Q&A failed", err);
        restoreHeightRef.current = null;
      } finally {
        loadingOlderRef.current = false;
        setLoadingOlder(false);
      }
    })();
  }, [hasMore, onItems, restoreHeightRef, scrollerRef, sessionId]);
  loadOlderRef.current = loadOlder;

  // Tab switch / panel open → show latest in that list.
  useEffect(() => {
    pinToBottom("auto");
  }, [pinToBottom, listFilter]);

  // Drop selection if the item disappeared (dismissed / left the list)
  useEffect(() => {
    if (!selectedId) return;
    if (!visible.some((q) => q.qa_id === selectedId)) {
      setSelectedId(null);
    }
  }, [visible, selectedId]);

  const selected =
    visible.find((q) => q.qa_id === selectedId) ?? null;

  // Prefill editor when selecting an answered question for edit
  useEffect(() => {
    if (!selected) {
      setAnswer("");
      return;
    }
    setAnswer(selected.answer?.body || "");
    const t = window.setTimeout(() => textareaRef.current?.focus(), 50);
    return () => window.clearTimeout(t);
  }, [selected?.qa_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const runPatch = async (
    qaId: string,
    action: "answer" | "pin" | "unpin" | "show" | "hide",
    body?: string,
  ) => {
    if (busyId) return;
    const current = itemsRef.current.find((q) => q.qa_id === qaId);
    if (!current) return;

    setBusyId(qaId);
    setError("");

    const roomWide =
      action === "pin" ||
      action === "unpin" ||
      action === "show" ||
      action === "hide";

    // Build optimistic payload and fan out over LiveKit BEFORE REST,
    // so students update with no API round-trip delay.
    let optimistic: QaItem = { ...current };
    if (action === "pin") {
      optimistic = { ...current, pinned: true };
    } else if (action === "unpin") {
      optimistic = { ...current, pinned: false };
    } else if (action === "show") {
      optimistic = { ...current, visible_to_all: true };
    } else if (action === "hide") {
      optimistic = { ...current, visible_to_all: false };
    } else if (action === "answer") {
      optimistic = {
        ...current,
        status: "answered",
        answer: {
          body: (body || "").trim(),
          answered_by: {
            identity: trainerId,
            role: "trainer",
            display_name: trainerName,
          },
          answered_at: new Date().toISOString(),
        },
      };
    }

    try {
      // Local UI only — do not block persistence on LiveKit.
      await onPublish(optimistic, { broadcast: false });

      const item = await patchQa({
        sessionId,
        qaId,
        action,
        body,
        trainerId,
        trainerName,
      });

      // Best-effort realtime fan-out after Mongo save succeeds.
      await onPublish(item, roomWide ? { roomWide: true } : undefined);

      if (action === "answer") {
        setSelectedId(null);
        setAnswer("");
        setListFilter("answered");
      }
    } catch (err) {
      console.error(err);
      // Roll back local state to pre-click.
      try {
        await onPublish(current, { broadcast: false });
      } catch {
        /* ignore rollback errors */
      }
      setError("Could not save. Try again.");
    } finally {
      setBusyId("");
    }
  };

  const submitAnswer = async () => {
    if (!selected || !answer.trim() || busyId) return;
    await runPatch(selected.qa_id, "answer", answer.trim());
  };

  const selectQuestion = (qaId: string) => {
    setSelectedId(qaId);
    setError("");
  };

  const sectionLabel: React.CSSProperties = {
    margin: "0 0 8px",
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "#64748b",
  };

  const renderCard = (q: QaItem, answeredCard: boolean) => {
    const selectedCard = selected?.qa_id === q.qa_id;
    const busy = busyId === q.qa_id;

    let border = "1px solid rgba(255,255,255,0.1)";
    let background = "#1a1d26";
    if (q.pinned) {
      border = "1px solid rgba(245,158,11,0.3)";
      background = "rgba(245,158,11,0.1)";
    } else if (selectedCard) {
      border = "1px solid rgba(59,130,246,0.5)";
      background = "rgba(59,130,246,0.12)";
    } else if (answeredCard) {
      border = "1px solid rgba(16,185,129,0.2)";
      background = "rgba(16,185,129,0.05)";
    }

    return (
      <div
        key={q.qa_id}
        ref={(node) => {
          if (node) cardRefs.current.set(q.qa_id, node);
          else cardRefs.current.delete(q.qa_id);
        }}
        style={{
          width: "100%",
          borderRadius: 12,
          border,
          background,
          padding: 12,
          textAlign: "left",
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 8,
          }}
        >
          <button
            type="button"
            onClick={() => selectQuestion(q.qa_id)}
            style={{
              minWidth: 0,
              flex: 1,
              textAlign: "left",
              border: "none",
              background: "transparent",
              padding: 0,
              cursor: "pointer",
              color: "inherit",
              font: "inherit",
              // Override global button nowrap so multi-line Q&A text keeps breaks
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 8,
              }}
            >
              {q.pinned ? (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    fontSize: 10,
                    fontWeight: 700,
                    color: "#fbbf24",
                  }}
                >
                  <Pin size={12} />
                  Pinned
                </span>
              ) : null}
              {q.visible_to_all ? (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    fontSize: 10,
                    fontWeight: 700,
                    color: "#93c5fd",
                  }}
                >
                  <Eye size={12} />
                  Visible to all
                </span>
              ) : null}
              {selectedCard ? (
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: "#93c5fd",
                  }}
                >
                  Selected
                </span>
              ) : null}
            </div>
            <p
              style={{
                margin: "4px 0 0",
                fontSize: 13,
                fontWeight: 600,
                color: "#fff",
                lineHeight: 1.4,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {q.question}
            </p>
            <p style={{ margin: "4px 0 0", fontSize: 12, color: "#64748b" }}>
              {authorLabel(q)}
              {q.created_at ? ` · ${formatAskedAt(q.created_at)}` : ""}
            </p>
            {answeredCard && q.answer?.body && !selectedCard ? (
              <div
                style={{
                  marginTop: 8,
                  borderRadius: 8,
                  background: "rgba(16,185,129,0.1)",
                  padding: "6px 8px",
                }}
              >
                <p
                  style={{
                    margin: 0,
                    fontSize: 10,
                    fontWeight: 700,
                    color: "#34d399",
                  }}
                >
                  {q.answer.answered_by?.display_name || "Trainer"}
                </p>
                <p
                  style={{
                    margin: "2px 0 0",
                    fontSize: 12,
                    color: "#cbd5e1",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {q.answer.body}
                </p>
              </div>
            ) : null}
          </button>
          <div style={{ display: "flex", flexShrink: 0, flexDirection: "column", gap: 4 }}>
            <div style={{ display: "flex", gap: 4 }}>
              <button
                type="button"
                disabled={busy}
                onClick={() =>
                  void runPatch(q.qa_id, q.pinned ? "unpin" : "pin")
                }
                title={q.pinned ? "Unpin" : "Pin to top"}
                style={iconBtnStyle(
                  Boolean(q.pinned),
                  "rgba(245,158,11,0.2)",
                  "#fbbf24",
                )}
              >
                <Pin size={14} />
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() =>
                  void runPatch(q.qa_id, q.visible_to_all ? "hide" : "show")
                }
                title={
                  q.visible_to_all
                    ? "Hide from other students"
                    : "Visible to all students"
                }
                style={iconBtnStyle(
                  Boolean(q.visible_to_all),
                  "rgba(59,130,246,0.2)",
                  "#93c5fd",
                )}
              >
                {q.visible_to_all ? <Eye size={14} /> : <EyeOff size={14} />}
              </button>
            </div>
            {!selectedCard ? (
              <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                {!answeredCard ? (
                  <button
                    type="button"
                    disabled={busy}
                    title="Mark as answered live in the session"
                    onClick={() =>
                      void runPatch(q.qa_id, "answer", "Answered in session")
                    }
                    style={{
                      border: "1px solid rgba(255,255,255,0.14)",
                      borderRadius: 8,
                      padding: "6px 8px",
                      background: "transparent",
                      color: "#cbd5e1",
                      fontSize: 11,
                      fontWeight: 700,
                      cursor: busy ? "not-allowed" : "pointer",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Answered live
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={() => selectQuestion(q.qa_id)}
                  style={{
                    border: "none",
                    borderRadius: 8,
                    padding: "6px 8px",
                    background: answeredCard
                      ? "rgba(255,255,255,0.06)"
                      : "#2563eb",
                    color: answeredCard ? "#94a3b8" : "#fff",
                    fontSize: 11,
                    fontWeight: 700,
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                  }}
                >
                  {answeredCard ? "Edit" : "Answer"}
                </button>
              </div>
            ) : null}
          </div>
        </div>

        {selectedCard ? (
          <div
            style={{
              marginTop: 12,
              paddingTop: 12,
              borderTop: "1px solid rgba(255,255,255,0.1)",
            }}
          >
            <p
              style={{
                margin: 0,
                fontSize: 12,
                fontWeight: 700,
                color: "#93c5fd",
              }}
            >
              {answeredCard ? "Update answer" : `Reply to ${authorLabel(q)}`}
            </p>
            <textarea
              ref={textareaRef}
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              rows={3}
              placeholder="Type your answer..."
              style={{
                marginTop: 8,
                width: "100%",
                boxSizing: "border-box",
                borderRadius: 10,
                border: "1px solid rgba(255,255,255,0.12)",
                background: "#0f1218",
                color: "#e2e8f0",
                padding: "10px 12px",
                fontSize: 13,
                fontFamily: "inherit",
                resize: "vertical",
                outline: "none",
                whiteSpace: "pre-wrap",
              }}
            />
            {error ? (
              <p style={{ margin: "6px 0 0", fontSize: 12, color: "#f87171" }}>
                {error}
              </p>
            ) : null}
            <div
              style={{
                marginTop: 8,
                display: "flex",
                gap: 8,
              }}
            >
              <button
                type="button"
                onClick={() => {
                  setSelectedId(null);
                  setAnswer("");
                  setError("");
                }}
                style={{
                  flex: 1,
                  border: "1px solid rgba(255,255,255,0.12)",
                  borderRadius: 10,
                  padding: "10px 12px",
                  background: "transparent",
                  color: "#94a3b8",
                  fontWeight: 600,
                  fontSize: 12,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              {!answeredCard ? (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() =>
                    void runPatch(q.qa_id, "answer", "Answered in session")
                  }
                  style={{
                    flex: 1,
                    border: "1px solid rgba(255,255,255,0.14)",
                    borderRadius: 10,
                    padding: "10px 12px",
                    background: "transparent",
                    color: "#cbd5e1",
                    fontWeight: 700,
                    fontSize: 12,
                    cursor: busy ? "not-allowed" : "pointer",
                    whiteSpace: "nowrap",
                  }}
                >
                  Answered live
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => void submitAnswer()}
                disabled={!answer.trim() || busy}
                style={{
                  flex: 2,
                  border: "none",
                  borderRadius: 10,
                  padding: "10px 12px",
                  background: !answer.trim() || busy ? "#475569" : "#2563eb",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: 12,
                  cursor: !answer.trim() || busy ? "not-allowed" : "pointer",
                }}
              >
                {busy
                  ? "Posting…"
                  : answeredCard
                    ? "Update answer"
                    : "Post answer"}
              </button>
            </div>
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 16,
        minHeight: 0,
        height: "100%",
      }}
    >
      <p
        style={{
          margin: 0,
          display: "flex",
          alignItems: "center",
          gap: 8,
          fontSize: 12,
          color: "#94a3b8",
          flexShrink: 0,
        }}
      >
        <MessageSquare size={14} />
        Select a question to answer it live
      </p>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 8,
          flexShrink: 0,
        }}
      >
        <StatPill
          label="Pending"
          value={String(pending.length)}
          accent="amber"
          selected={listFilter === "pending"}
          onClick={() => {
            setListFilter("pending");
            setSelectedId(null);
          }}
        />
        <StatPill
          label="Answered"
          value={String(answered.length)}
          accent="emerald"
          selected={listFilter === "answered"}
          onClick={() => {
            setListFilter("answered");
            setSelectedId(null);
          }}
        />
      </div>

      <div
        ref={scrollerRef}
        onScroll={onScroll}
        className="meeting-tool-hide-scrollbar"
        style={{
          flex: 1,
          minHeight: 120,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 16,
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        {(hasMore || loadingOlder) ? (
          <button
            type="button"
            onClick={loadOlder}
            disabled={loadingOlder}
            style={{
              width: "100%",
              border: "none",
              background: "transparent",
              color: "#64748b",
              fontSize: 11,
              padding: "4px 0 8px",
              cursor: loadingOlder ? "default" : "pointer",
            }}
          >
            {loadingOlder ? "Loading earlier questions…" : "Load earlier questions"}
          </button>
        ) : null}
        {listFilter === "pending" ? (
          pending.length > 0 ? (
            <div>
              <p style={sectionLabel}>Pending ({pending.length})</p>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {pending.map((q) => renderCard(q, false))}
              </div>
            </div>
          ) : (
            <p style={{ margin: 0, fontSize: 12, color: "#64748b" }}>
              No pending questions.
            </p>
          )
        ) : answered.length > 0 ? (
          <div>
            <p style={sectionLabel}>Answered ({answered.length})</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {answered.map((q) => renderCard(q, true))}
            </div>
          </div>
        ) : (
          <p style={{ margin: 0, fontSize: 12, color: "#64748b" }}>
            No answered questions yet.
          </p>
        )}
        <div ref={bottomRef} aria-hidden style={{ height: 1 }} />
      </div>
    </div>
  );
}
